import React from 'react';
import { Box, Card, CardContent, Grid, Typography, useTheme } from '@mui/material';
import {
  AttachMoney as AttachMoneyIcon,
  Shield as ShieldIcon,
  Assignment as AssignmentIcon,
  ThumbUp as ThumbUpIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
} from '@mui/icons-material';

interface VitalSignProps {
  label: string;
  value: string;
  delta?: string;
  direction?: 'up' | 'down' | 'stable';
  icon: React.ReactNode;
  color: string;
}

const VitalSignCard: React.FC<VitalSignProps> = ({ label, value, delta, direction, icon, color }) => {
  const directionConfig = {
    up: { Icon: TrendingUpIcon, bg: '#E8F5E9', text: '#2E7D32' },
    down: { Icon: TrendingDownIcon, bg: '#FFEBEE', text: '#C62828' },
    stable: { Icon: TrendingFlatIcon, bg: '#E3F2FD', text: '#1565C0' },
  };
  const trend = direction ? directionConfig[direction] : null;

  return (
    <Card
      sx={{
        height: '100%',
        background: `linear-gradient(135deg, ${color}08 0%, ${color}03 100%)`,
        border: `1px solid ${color}20`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 24px ${color}25`,
          borderColor: `${color}40`,
        },
      }}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        {/* Icon + Label row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              borderRadius: '8px',
              background: `${color}12`,
              color: color,
            }}
          >
            {icon}
          </Box>
          <Typography
            sx={{
              fontSize: '0.8rem',
              fontWeight: 700,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            {label}
          </Typography>
        </Box>

        {/* Value */}
        <Typography
          sx={{
            fontSize: '2.4rem',
            fontWeight: 800,
            color: color,
            lineHeight: 1.1,
            letterSpacing: '-1px',
            mb: 0.5,
          }}
        >
          {value}
        </Typography>

        {/* Delta badge */}
        {delta && trend && (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1.5,
              py: 0.5,
              borderRadius: '20px',
              background: trend.bg,
              color: trend.text,
              mt: 1,
            }}
          >
            <trend.Icon sx={{ fontSize: 16 }} />
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700 }}>
              {delta}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

interface VitalSignsRibbonProps {
  systemWideSales: number;
  swsDelta: { value: number; direction: 'up' | 'down' | 'stable' };
  royaltyIntegrityScore: number;
  royaltyDelta: { direction: 'up' | 'down' | 'stable' };
  activeWorkOrders: { count: number; delta: number; direction: 'up' | 'down' | 'stable' };
  networkNPS: { score: number; delta: number; direction: 'up' | 'down' | 'stable' };
}

export const VitalSignsRibbon: React.FC<VitalSignsRibbonProps> = ({
  systemWideSales,
  swsDelta,
  royaltyIntegrityScore,
  royaltyDelta,
  activeWorkOrders,
  networkNPS,
}) => {
  // Format SWS as compact currency
  const formatSWS = (value: number): string => {
    if (value >= 1000000) {
      const m = value / 1000000;
      return `$${m.toFixed(m < 10 ? 2 : 1)}M`;
    }
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${Math.round(value).toLocaleString()}`;
  };

  const getDeltaPrefix = (direction: 'up' | 'down' | 'stable'): string => {
    if (direction === 'down') return '-';
    if (direction === 'up') return '+';
    return '';
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        sx={{
          fontSize: '0.7rem',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '1.5px',
          color: 'text.disabled',
          mb: 1.5,
        }}
      >
        Vital Signs
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <VitalSignCard
            label="System-Wide Sales"
            value={formatSWS(systemWideSales)}
            delta={`${getDeltaPrefix(swsDelta.direction)}${swsDelta.value}% vs last mo`}
            direction={swsDelta.direction}
            icon={<AttachMoneyIcon sx={{ fontSize: 20 }} />}
            color="#1976D2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <VitalSignCard
            label="Royalty Integrity"
            value={`${royaltyIntegrityScore}%`}
            delta="On target"
            direction={royaltyDelta.direction}
            icon={<ShieldIcon sx={{ fontSize: 20 }} />}
            color="#7B1FA2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <VitalSignCard
            label="Active Work Orders"
            value={activeWorkOrders.count.toLocaleString()}
            delta={`${getDeltaPrefix(activeWorkOrders.direction)}${activeWorkOrders.delta} this week`}
            direction={activeWorkOrders.direction}
            icon={<AssignmentIcon sx={{ fontSize: 20 }} />}
            color="#F57C00"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <VitalSignCard
            label="Network NPS"
            value={`${networkNPS.score}`}
            delta={`${getDeltaPrefix(networkNPS.direction)}${networkNPS.delta} pts`}
            direction={networkNPS.direction}
            icon={<ThumbUpIcon sx={{ fontSize: 20 }} />}
            color="#2E7D32"
          />
        </Grid>
      </Grid>
    </Box>
  );
};
